import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const PHP_API = process.env.REACT_APP_PHP_API_URL + "zuribuildAPI/";
//const SITES_URL = "http://localhost/zuribuildAPI/user_websites/";
const SITES_URL = process.env.REACT_APP_SITE_URL;

const Field = ({ label, fieldKey, value, onChange, textarea }) => (
  <div style={styles.fieldGroup}>
    <label style={styles.fieldLabel}>{label}</label>
    {textarea ? (
      <textarea style={styles.textarea} value={value || ""} onChange={(e) => onChange(fieldKey, e.target.value)} rows={3} />
    ) : (
      <input style={styles.input} value={value || ""} onChange={(e) => onChange(fieldKey, e.target.value)} />
    )}
  </div>
);

const ItemCard = ({ itemKey, label, children, isVisible, onToggle }) => (
  <div style={{ ...styles.card, opacity: isVisible ? 1 : 0.45 }}>
    <div style={styles.cardHeader}>
      <p style={styles.cardNum}>{label}</p>
      <div style={{ ...styles.toggle, background: isVisible ? "#7C3AED" : "#ccc" }} onClick={() => onToggle(itemKey)}>
        <div style={{ ...styles.toggleDot, left: isVisible ? 18 : 2 }} />
      </div>
    </div>
    {children}
  </div>
);

const ImageField = ({ label, fieldKey, value, onChange, onPickFromMedia, pickLabel }) => (
  <div style={styles.fieldGroup}>
    <label style={styles.fieldLabel}>{label}</label>
    <div style={styles.imageFieldWrap}>
      {value && <img src={value} alt="preview" style={styles.imagePreview} onError={(e) => { e.target.style.display = "none"; }} />}
      <div style={{ display: "flex", gap: 6 }}>
        <input style={{ ...styles.input, flex: 1 }} value={value || ""} placeholder={pickLabel || "Paste image URL..."} onChange={(e) => onChange(fieldKey, e.target.value)} />
        <button style={styles.btnPickMedia} onClick={() => onPickFromMedia(fieldKey)} title={pickLabel}><i className="bi bi-images" /></button>
      </div>
    </div>
  </div>
);

export default function EditWebsite() {
  const { folderName } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const firebaseUser = JSON.parse(localStorage.getItem('firebase_user') || '{}');
  const memberId = firebaseUser?.uid || '';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("info");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [media, setMedia] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [activeImageField, setActiveImageField] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('user_info') || !memberId) {
      navigate('/login');
      return;
    }
    const payload = { folder_name: folderName, member_id: memberId };
    axios.post(PHP_API + "check_ownership.php", payload, {
      headers: { "Content-Type": "application/json" }
    })
    .then((res) => {
      console.log(res);
      if (!res.data.success) { navigate('/dashboard'); }
    })
    .catch((err) => { navigate('/dashboard'); });
  }, []);

  useEffect(() => {
    axios.post(PHP_API + "get_site_data.php", { folder_name: folderName, member_id: memberId })
      .then((res) => {
        if (res.data.success) {
          const loaded = res.data.data;
          if (!loaded.site.items_visible) {
            loaded.site.items_visible = {
              service1: true, service2: true, service3: true,
              service4: true, service5: true, service6: true,
              testimonials1: true, testimonials2: true, testimonials3: true,
              testimonials4: true, testimonials5: true, testimonials6: true,
              faq1: true, faq2: true, faq3: true, faq4: true,
              team1: true, team2: true, team3: true,
              gallery1: true, gallery2: true, gallery3: true,
              gallery4: true, gallery5: true, gallery6: true,
            };
          }
          if (!loaded.site.theme) loaded.site.theme = "light";
          if (loaded.site.business.whatsapp_visible === undefined) {
            loaded.site.business.whatsapp_visible = "block";
          }
          setData(loaded);
        } else {
          toast.error(t("edit-website.site_not_found"));
        }
      })
      .catch(() => toast.error(t("edit-website.failed_to_load")))
      .finally(() => setLoading(false));
  }, [folderName]);

  const loadMedia = useCallback(() => {
    setMediaLoading(true);
    axios.post(PHP_API + "get_media.php", { folder_name: folderName })
      .then((res) => { if (res.data.success) setMedia(res.data.media || []); })
      .catch(() => toast.error(t("edit-website.failed_to_load")))
      .finally(() => setMediaLoading(false));
  }, [folderName]);

  useEffect(() => { loadMedia(); }, [loadMedia]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error(t("edit-website.file_too_large")); return; }
    const formData = new FormData();
    formData.append("image", file);
    formData.append("folder_name", folderName);
    setUploading(true);
    try {
      const res = await axios.post(PHP_API + "upload_image.php", formData, { headers: { "Content-Type": "multipart/form-data" } });
      if (res.data.success) {
        setMedia((prev) => [...prev, res.data.image]);
        toast.success(t("edit-website.image_uploaded"), { position: "bottom-right" });
      } else {
        toast.error(res.data.message || t("edit-website.upload_error"));
      }
    } catch { toast.error(t("edit-website.upload_error")); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const handleDeleteImage = async (filename) => {
    if (!window.confirm(t("edit-website.delete_image_confirm"))) return;
    try {
      const res = await axios.post(PHP_API + "delete_image.php", { folder_name: folderName, filename });
      if (res.data.success) {
        setMedia((prev) => prev.filter((img) => img.filename !== filename));
        toast.success(t("edit-website.image_deleted"), { position: "bottom-right" });
      }
    } catch { toast.error(t("edit-website.delete_failed")); }
  };

  const openMediaPicker = (fieldKey) => { setActiveImageField(fieldKey); setShowMediaModal(true); };
  const selectMediaImage = (imageUrl) => {
    if (activeImageField) updateBusiness(activeImageField, imageUrl);
    setShowMediaModal(false);
    setActiveImageField(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.post(PHP_API + "update_site.php", { folder_name: folderName, data, member_id: memberId });
      if (res.data.success) {
        toast.success(t("edit-website.site_updated"), { position: "bottom-right" });
        if (isMobile) setShowSectionModal(false);
      } else {
        toast.error(t("edit-website.failed_to_save"), { position: "bottom-right" });
      }
    } catch { toast.error(t("edit-website.server_error"), { position: "bottom-right" }); }
    finally { setSaving(false); }
  };

  const autoSave = async (updatedData) => {
    try {
      await axios.post(PHP_API + "update_site.php", { folder_name: folderName, data: updatedData, member_id: memberId });
      toast.success(t("edit-website.updated"), { position: "bottom-right", autoClose: 1000 });
    } catch { toast.error(t("edit-website.failed_to_update"), { position: "bottom-right" }); }
  };

  const updateBusiness = useCallback((key, value) => {
    setData((prev) => ({ ...prev, site: { ...prev.site, business: { ...prev.site.business, [key]: value } } }));
  }, []);

  const updateBrand = useCallback((key, value) => {
    setData((prev) => ({ ...prev, site: { ...prev.site, brand: { ...prev.site.brand, [key]: value } } }));
  }, []);

  const toggleTheme = () => {
    setData((prev) => {
      const next = (prev?.site?.theme || "light") === "dark" ? "light" : "dark";
      const updated = { ...prev, site: { ...prev.site, theme: next } };
      autoSave(updated);
      return updated;
    });
  };

  const toggleWhatsapp = () => {
    setData((prev) => {
      const next = (prev?.site?.business?.whatsapp_visible || "block") === "block" ? "none" : "block";
      const updated = { ...prev, site: { ...prev.site, business: { ...prev.site.business, whatsapp_visible: next } } };
      autoSave(updated);
      return updated;
    });
  };

  const toggleSection = (sectionKey) => {
    setData((prev) => {
      const updated = { ...prev, site: { ...prev.site, sections_visible: { ...prev.site.sections_visible, [sectionKey]: !prev.site.sections_visible[sectionKey] } } };
      autoSave(updated);
      return updated;
    });
  };

  const toggleItem = (itemKey) => {
    setData((prev) => {
      const updated = { ...prev, site: { ...prev.site, items_visible: { ...prev.site.items_visible, [itemKey]: !prev.site.items_visible?.[itemKey] } } };
      autoSave(updated);
      return updated;
    });
  };

  const handleSectionClick = (sectionKey) => {
    setActiveSection(sectionKey);
    if (isMobile) setShowSectionModal(true);
  };

  const b = data?.site?.business || {};
  const brand = data?.site?.brand || {};
  const sections = data?.site?.sections_visible || {};
  const items = data?.site?.items_visible || {};
  const isItemVisible = (key) => items[key] !== false;
  const isDark = data?.site?.theme === "dark";
  const isWhatsappVisible = b.whatsapp_visible !== "none";

  const SectionToggle = ({ sectionKey, label, icon }) => (
    <div
      style={{ ...styles.sidebarItem, ...(activeSection === sectionKey && !isMobile ? styles.sidebarItemActive : {}), ...(sections[sectionKey] === false ? styles.sidebarItemDimmed : {}) }}
      onClick={() => handleSectionClick(sectionKey)}
    >
      <span><i className={`bi ${icon}`} style={{ marginRight: 8 }} />{label}</span>
      {sections[sectionKey] !== undefined && (
        <div style={{ ...styles.toggle, background: sections[sectionKey] ? "#7C3AED" : "#ccc" }} onClick={(e) => { e.stopPropagation(); toggleSection(sectionKey); }}>
          <div style={{ ...styles.toggleDot, left: sections[sectionKey] ? 18 : 2 }} />
        </div>
      )}
    </div>
  );

  const renderMedia = () => (
    <>
      <h5 style={styles.sectionTitle}><i className="bi bi-images" /> {t("edit-website.media_library")}</h5>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <p style={{ color: "#888", fontSize: 13, margin: 0 }}>{media.length} {t("edit-website.images_uploaded")}</p>
        <button style={styles.btnUpload} onClick={() => fileInputRef.current?.click()} disabled={uploading}>
          {uploading ? <><span className="spinner-border spinner-border-sm" /> {t("edit-website.uploading")}</> : <><i className="bi bi-cloud-upload" /> {t("edit-website.upload_image")}</>}
        </button>
        <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" style={{ display: "none" }} onChange={handleUpload} />
      </div>
      {mediaLoading ? (
        <div style={{ textAlign: "center", padding: 40 }}><div className="spinner-border" style={{ color: "purple" }} /></div>
      ) : media.length === 0 ? (
        <div style={styles.emptyMedia}><i className="bi bi-image" style={{ fontSize: 40, color: "#ddd" }} /><p style={{ color: "#aaa", marginTop: 8 }}>{t("edit-website.no_images")}</p></div>
      ) : (
        <div style={styles.mediaGrid}>
          {media.map((img) => (
            <div key={img.id} style={styles.mediaItem}>
              <img src={img.url} alt={img.filename} style={styles.mediaThumbnail} />
              <button style={styles.btnDeleteMedia} onClick={() => handleDeleteImage(img.filename)}><i className="bi bi-x" /></button>
            </div>
          ))}
        </div>
      )}
    </>
  );

  const renderSection = () => {
    if (activeSection === "media") return renderMedia();
    switch (activeSection) {
      case "info": return (
        <>
          <h5 style={styles.sectionTitle}><i className="bi bi-building-fill" /> {t("edit-website.business_info")}</h5>
          <div style={styles.grid2}>
            <Field label={t("edit-website.business_name")} fieldKey="name" value={b["name"]} onChange={updateBusiness} />
            <Field label={t("edit-website.phone")} fieldKey="phone" value={b["phone"]} onChange={updateBusiness} />
            <Field label={t("edit-website.whatsapp")} fieldKey="whatsapp" value={b["whatsapp"]} onChange={updateBusiness} />
            <Field label={t("edit-website.email")} fieldKey="email" value={b["email"]} onChange={updateBusiness} />
            <Field label={t("edit-website.address")} fieldKey="address" value={b["address"]} onChange={updateBusiness} />
            <Field label={t("edit-website.opening_hours")} fieldKey="opening_hours" value={b["opening_hours"]} onChange={updateBusiness} />
          </div>
          <hr style={styles.divider} />
          <h6 style={styles.subTitle}>{t("edit-website.logo")}</h6>
          <ImageField
            label={t("edit-website.logo_url")}
            fieldKey="logo_url"
            value={b["logo_url"]}
            onChange={updateBusiness}
            onPickFromMedia={openMediaPicker}
            pickLabel={t("edit-website.pick_from_media")}
          />
          <p style={{ fontSize: 11, color: '#aaa', marginTop: -8, marginBottom: 14 }}>
            {t("edit-website.upload_your_logo")}
          </p>
          <hr style={styles.divider} />
          <h6 style={styles.subTitle}>{t("edit-website.brand_colors")}</h6>
          <div style={styles.grid2}>
            <div style={styles.fieldGroup}>
              <label style={styles.fieldLabel}>{t("edit-website.primary_color")}</label>
              <div style={styles.colorRow}>
                <input type="color" value={brand.primaryColor || "#7C3AED"} onChange={(e) => updateBrand("primaryColor", e.target.value)} style={styles.colorPicker} />
                <span style={styles.colorVal}>{brand.primaryColor}</span>
              </div>
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.fieldLabel}>{t("edit-website.secondary_color")}</label>
              <div style={styles.colorRow}>
                <input type="color" value={brand.secondaryColor || "#E11D48"} onChange={(e) => updateBrand("secondaryColor", e.target.value)} style={styles.colorPicker} />
                <span style={styles.colorVal}>{brand.secondaryColor}</span>
              </div>
            </div>
          </div>
          <hr style={styles.divider} />
          <h6 style={styles.subTitle}>{t("edit-website.social_media")}</h6>
          <div style={styles.grid2}>
            <Field label={t("edit-website.facebook")} fieldKey="social_facebook" value={b["social_facebook"]} onChange={updateBusiness} />
            <Field label={t("edit-website.instagram")} fieldKey="social_instagram" value={b["social_instagram"]} onChange={updateBusiness} />
            <Field label={t("edit-website.whatsapp")} fieldKey="social_whatsapp" value={b["social_whatsapp"]} onChange={updateBusiness} />
            <Field label={t("edit-website.linkedin")} fieldKey="social_linkedin" value={b["social_linkedin"]} onChange={updateBusiness} />
            <Field label={t("edit-website.pixel_id")} fieldKey="pixel_id" value={b["pixel_id"]} onChange={updateBusiness} />
            <Field label={t("edit-website.ga4_id")} fieldKey="ga4_id" value={b["ga4_id"]} onChange={updateBusiness} />
            <Field label={t("edit-website.tiktok_pixel_id")} fieldKey="tiktok_pixel_id" value={b["tiktok_pixel_id"]} onChange={updateBusiness} />
          </div>
        </>
      );
      case "hero": return (
        <>
          <h5 style={styles.sectionTitle}><i className="bi bi-layout-text-window" /> {t("edit-website.hero")}</h5>
          <Field label={t("edit-website.hero_title")} fieldKey="hero_title" value={b["hero_title"]} onChange={updateBusiness} />
          <Field label={t("edit-website.hero_description")} fieldKey="hero_description" value={b["hero_description"]} onChange={updateBusiness} textarea />
          <hr style={styles.divider} />
          <ImageField label={t("edit-website.hero_image")} fieldKey="hero_image" value={b["hero_image"]} onChange={updateBusiness} onPickFromMedia={openMediaPicker} pickLabel={t("edit-website.paste_image_url")} />
        </>
      );
      case "about": return (
        <>
          <h5 style={styles.sectionTitle}><i className="bi bi-info-circle" /> {t("edit-website.about_us")}</h5>
          <Field label={t("edit-website.title")} fieldKey="about_us_block_title" value={b["about_us_block_title"]} onChange={updateBusiness} />
          <Field label={t("edit-website.short_description")} fieldKey="about_us_block_description" value={b["about_us_block_description"]} onChange={updateBusiness} />
          <Field label={t("edit-website.paragraph")} fieldKey="about_us_block_paragraph" value={b["about_us_block_paragraph"]} onChange={updateBusiness} textarea />
          <hr style={styles.divider} />
          <ImageField label={t("edit-website.about_image")} fieldKey="about_image" value={b["about_image"]} onChange={updateBusiness} onPickFromMedia={openMediaPicker} pickLabel={t("edit-website.paste_image_url")} />
        </>
      );
      case "services": return (
        <>
          <h5 style={styles.sectionTitle}><i className="bi bi-list-check" /> {t("edit-website.services")}</h5>
          <Field label={t("edit-website.section_title")} fieldKey="service_block_title" value={b["service_block_title"]} onChange={updateBusiness} />
          <Field label={t("edit-website.section_description")} fieldKey="service_block_description" value={b["service_block_description"]} onChange={updateBusiness} />
          <hr style={styles.divider} />
          {[1,2,3,4,5,6].map((n) => (
            <ItemCard key={n} itemKey={`service${n}`} label={`${t("edit-website.service")} ${n}`} isVisible={isItemVisible(`service${n}`)} onToggle={toggleItem}>
              <Field label={t("edit-website.title")} fieldKey={`service${n}_title`} value={b[`service${n}_title`]} onChange={updateBusiness} />
              <Field label={t("edit-website.description")} fieldKey={`service${n}_description`} value={b[`service${n}_description`]} onChange={updateBusiness} textarea />
              <ImageField label={t("edit-website.image")} fieldKey={`service${n}_image`} value={b[`service${n}_image`]} onChange={updateBusiness} onPickFromMedia={openMediaPicker} pickLabel={t("edit-website.paste_image_url")} />
            </ItemCard>
          ))}
        </>
      );
      case "marketing": return (
        <>
          <h5 style={styles.sectionTitle}><i className="bi bi-megaphone" /> {t("edit-website.marketing")}</h5>
          {[1,2,3].map((n) => (
            <div key={n} style={styles.card}>
              <p style={styles.cardNum}>{t("edit-website.message")} {n}</p>
              <Field label={t("edit-website.title")} fieldKey={`marketing_message${n}_title`} value={b[`marketing_message${n}_title`]} onChange={updateBusiness} />
              <Field label={t("edit-website.description")} fieldKey={`marketing_message${n}_description`} value={b[`marketing_message${n}_description`]} onChange={updateBusiness} textarea />
            </div>
          ))}
        </>
      );
      case "stats": return (
        <>
          <h5 style={styles.sectionTitle}><i className="bi bi-bar-chart" /> {t("edit-website.stats")}</h5>
          {[1,2,3].map((n) => (
            <div key={n} style={styles.card}>
              <p style={styles.cardNum}>{t("edit-website.stat")} {n}</p>
              <div style={styles.grid2}>
                <Field label={t("edit-website.value")} fieldKey={`performance_stats${n}_value`} value={b[`performance_stats${n}_value`]} onChange={updateBusiness} />
                <Field label={t("edit-website.description")} fieldKey={`performance_stats${n}_description`} value={b[`performance_stats${n}_description`]} onChange={updateBusiness} />
              </div>
            </div>
          ))}
        </>
      );
      case "testimonials": return (
        <>
          <h5 style={styles.sectionTitle}><i className="bi bi-chat-quote" /> {t("edit-website.testimonials")}</h5>
          <Field label={t("edit-website.section_title")} fieldKey="testimonials_block_title" value={b["testimonials_block_title"]} onChange={updateBusiness} />
          <Field label={t("edit-website.section_description")} fieldKey="testimonials_block_description" value={b["testimonials_block_description"]} onChange={updateBusiness} />
          <hr style={styles.divider} />
          {[1,2,3,4,5,6].map((n) => (
            <ItemCard key={n} itemKey={`testimonials${n}`} label={`${t("edit-website.testimonial")} ${n}`} isVisible={isItemVisible(`testimonials${n}`)} onToggle={toggleItem}>
              <div style={styles.grid2}>
                <Field label={t("edit-website.name")} fieldKey={`testimonials${n}_name`} value={b[`testimonials${n}_name`]} onChange={updateBusiness} />
                <Field label={t("edit-website.company")} fieldKey={`testimonials${n}_company`} value={b[`testimonials${n}_company`]} onChange={updateBusiness} />
              </div>
              <Field label={t("edit-website.message")} fieldKey={`testimonials${n}_description`} value={b[`testimonials${n}_description`]} onChange={updateBusiness} textarea />
              <ImageField label={t("edit-website.photo")} fieldKey={`testimonials${n}_image`} value={b[`testimonials${n}_image`]} onChange={updateBusiness} onPickFromMedia={openMediaPicker} pickLabel={t("edit-website.paste_image_url")} />
            </ItemCard>
          ))}
        </>
      );
      case "contact": return (
        <>
          <h5 style={styles.sectionTitle}><i className="bi bi-envelope" /> {t("edit-website.contact")}</h5>
          <Field label={t("edit-website.section_title")} fieldKey="contact_block_title" value={b["contact_block_title"]} onChange={updateBusiness} />
          <Field label={t("edit-website.description")} fieldKey="contact_block_description" value={b["contact_block_description"]} onChange={updateBusiness} textarea />
          <Field label={t("edit-website.button_text")} fieldKey="contact_btn_ctatext" value={b["contact_btn_ctatext"]} onChange={updateBusiness} />
        </>
      );
      case "gallery": return (
        <>
          <h5 style={styles.sectionTitle}><i className="bi bi-images" /> {t("edit-website.gallery")}</h5>
          <p style={{ color: "#888", fontSize: 13 }}>{t("edit-website.gallery_hint")}</p>
          {[1,2,3,4,5,6].map((n) => (
            <ItemCard key={n} itemKey={`gallery${n}`} label={`${t("edit-website.image")} ${n}`} isVisible={isItemVisible(`gallery${n}`)} onToggle={toggleItem}>
              <ImageField label={t("edit-website.image_url")} fieldKey={`gallery${n}_url`} value={b[`gallery${n}_url`]} onChange={updateBusiness} onPickFromMedia={openMediaPicker} pickLabel={t("edit-website.paste_image_url")} />
            </ItemCard>
          ))}
        </>
      );
      case "faq": return (
        <>
          <h5 style={styles.sectionTitle}><i className="bi bi-question-circle" /> {t("edit-website.faq")}</h5>
          {[1,2,3,4].map((n) => (
            <ItemCard key={n} itemKey={`faq${n}`} label={`FAQ ${n}`} isVisible={isItemVisible(`faq${n}`)} onToggle={toggleItem}>
              <Field label={t("edit-website.question")} fieldKey={`faq${n}_question`} value={b[`faq${n}_question`]} onChange={updateBusiness} />
              <Field label={t("edit-website.answer")} fieldKey={`faq${n}_answer`} value={b[`faq${n}_answer`]} onChange={updateBusiness} textarea />
            </ItemCard>
          ))}
        </>
      );
      case "team": return (
        <>
          <h5 style={styles.sectionTitle}><i className="bi bi-people" /> {t("edit-website.team")}</h5>
          {[1,2,3].map((n) => (
            <ItemCard key={n} itemKey={`team${n}`} label={`${t("edit-website.member")} ${n}`} isVisible={isItemVisible(`team${n}`)} onToggle={toggleItem}>
              <div style={styles.grid2}>
                <Field label={t("edit-website.name")} fieldKey={`team${n}_name`} value={b[`team${n}_name`]} onChange={updateBusiness} />
                <Field label={t("edit-website.role")} fieldKey={`team${n}_role`} value={b[`team${n}_role`]} onChange={updateBusiness} />
              </div>
              <Field label={t("edit-website.description")} fieldKey={`team${n}_description`} value={b[`team${n}_description`]} onChange={updateBusiness} textarea />
              <ImageField label={t("edit-website.photo")} fieldKey={`team${n}_image`} value={b[`team${n}_image`]} onChange={updateBusiness} onPickFromMedia={openMediaPicker} pickLabel={t("edit-website.paste_image_url")} />
            </ItemCard>
          ))}
        </>
      );
      case "nav": return (
        <>
          <h5 style={styles.sectionTitle}><i className="bi bi-compass" /> {t("edit-website.navigation")}</h5>
          <p style={{ color: "#888", fontSize: 13, marginBottom: 16 }}>{t("edit-website.nav_item")}</p>
          {[0,1,2,3].map((n) => (
            <div key={n} style={styles.card}>
              <p style={styles.cardNum}>{t("edit-website.nav_item")} {n + 1}</p>
              <div style={styles.grid2}>
                <Field label={t("edit-website.nav_label")} fieldKey={`nav${n}_label`} value={b[`nav${n}_label`]} onChange={updateBusiness} />
                <Field label={t("edit-website.nav_path")} fieldKey={`nav${n}_path`} value={b[`nav${n}_path`]} onChange={updateBusiness} />
              </div>
            </div>
          ))}
          <hr style={styles.divider} />
          <h6 style={styles.subTitle}>{t("edit-website.store_nav_label")}</h6>
          <div style={styles.card}>
            <Field label={t("edit-website.store_nav_label")} fieldKey="store_nav_label" value={b["store_nav_label"] || (b["store_nav_visible"] === "block" ? "Boutique" : "")} onChange={updateBusiness} />
          </div>
        </>
      );
      default: return null;
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingWrap}>
        <div className="spinner-border" style={{ color: "purple" }} />
        <p style={{ color: "black", marginTop: 12 }}>{t("edit-website.loading")}</p>
      </div>
    );
  }

  const sectionLabels = {
    info: t("edit-website.business_info"), hero: t("edit-website.hero"),
    about: t("edit-website.about_us"), services: t("edit-website.services"),
    marketing: t("edit-website.marketing"), stats: t("edit-website.stats"),
    testimonials: t("edit-website.testimonials"), contact: t("edit-website.contact"),
    media: t("edit-website.media_library"), gallery: t("edit-website.gallery"),
    faq: t("edit-website.faq"), team: t("edit-website.team"),
    products: t("edit-website.products"), nav: t("edit-website.navigation"),
  };

  return (
    <>
      <style>{`
        * { box-sizing: border-box; }
        body { background: #fff; margin: 0; font-family: 'Inter', -apple-system, sans-serif; }
        textarea, input { outline: none; }
        textarea:focus, input:focus { border-color: #7C3AED !important; }
        @media (max-width: 768px) {
          .edit-main { flex-direction: column !important; }
          .edit-sidebar { width: 100% !important; display: block !important; padding: 10px !important; border-right: none !important; border-bottom: 1px solid #eee !important; }
          .edit-content { display: none !important; }
          .sidebar-group-label { display: none !important; }
        }
        @media (min-width: 769px) { .mobile-section-modal { display: none !important; } }
      `}</style>

      <div style={styles.topbar}>
        <div style={styles.topbarLeft}>
          <Link to="/dashboard" style={{ textDecoration: "none" }}>
            <img src="/zuribuild.png" style={{ width: "40px", marginTop: "-10px" }} alt="logo" />
            <span style={styles.logo}>ZuriBuild</span>
          </Link>
          <span style={styles.dividerV} />
        </div>
        <div style={styles.topbarRight}>
          <a href={SITES_URL + folderName + "/"} target="_blank" rel="noreferrer" style={styles.btnPreview}>
            <i className="bi bi-eye" /> {t("edit-website.preview")}
          </a>
          <button style={{ ...styles.btnSave, opacity: saving ? 0.7 : 1 }} onClick={handleSave} disabled={saving}>
            {saving ? <><span className="spinner-border spinner-border-sm" /> {t("edit-website.saving")}</> : <><i className="bi bi-floppy" /> {t("edit-website.save")}</>}
          </button>
        </div>
      </div>

      <div className="edit-main" style={styles.main}>
        <div className="edit-sidebar" style={styles.sidebar}>
          <h2 className="sidebar-site-title" style={styles.siteTitle}>{data?.site?.name || folderName}</h2>

          {/* Theme Toggle */}
          <div style={styles.toggleRow}>
            <span style={styles.toggleLabel}>
              <i className={`bi ${isDark ? "bi-moon-fill" : "bi-sun-fill"}`} style={{ marginRight: 6, color: isDark ? "#7C3AED" : "#f59e0b" }} />
              {isDark ? t("edit-website.dark_mode") : t("edit-website.light_mode")}
            </span>
            <div style={{ ...styles.toggle, background: isDark ? "#7C3AED" : "#ccc" }} onClick={toggleTheme}>
              <div style={{ ...styles.toggleDot, left: isDark ? 18 : 2 }} />
            </div>
          </div>

          {/* WhatsApp Toggle */}
          <div style={styles.toggleRow}>
            <span style={styles.toggleLabel}>
              <i className="bi bi-whatsapp" style={{ marginRight: 6, color: "#25D366" }} />
              WhatsApp
            </span>
            <div style={{ ...styles.toggle, background: isWhatsappVisible ? "#25D366" : "#ccc" }} onClick={toggleWhatsapp}>
              <div style={{ ...styles.toggleDot, left: isWhatsappVisible ? 18 : 2 }} />
            </div>
          </div>

          {/* Media */}
          <div style={{ ...styles.sidebarItem, ...(activeSection === "media" && !isMobile ? styles.sidebarItemActive : {}), marginBottom: 8, borderBottom: "1px solid #eee", paddingBottom: 12 }} onClick={() => handleSectionClick("media")}>
            <span><i className="bi bi-image" style={{ marginRight: 8 }} />{t("edit-website.media")}</span>
          </div>

          {/* Store */}
          <Link to={`/store/${folderName}`} style={{ ...styles.sidebarItem, textDecoration: "none", marginBottom: 8, borderBottom: "1px solid #eee", paddingBottom: 12 }}>
            <span><i className="bi bi-bag" style={{ marginRight: 8 }} />{t("edit-website.store")}</span>
          </Link>

          {/* Navigation */}
          <div style={{ ...styles.sidebarItem, ...(activeSection === "nav" && !isMobile ? styles.sidebarItemActive : {}), marginBottom: 8, borderBottom: "1px solid #eee", paddingBottom: 12 }} onClick={() => handleSectionClick("nav")}>
            <span><i className="bi bi-compass" style={{ marginRight: 8 }} />{t("edit-website.navigation")}</span>
          </div>

          <p className="sidebar-group-label" style={styles.sidebarGroupLabel}>{t("edit-website.active_sections")}</p>
          {[
            { key: "info", label: t("edit-website.business_info"), icon: "bi-building-fill" },
            { key: "hero", label: t("edit-website.hero"), icon: "bi-layout-text-window" },
            { key: "about", label: t("edit-website.about_us"), icon: "bi-info-circle" },
            { key: "services", label: t("edit-website.services"), icon: "bi-list-check" },
            { key: "marketing", label: t("edit-website.marketing"), icon: "bi-megaphone" },
            { key: "stats", label: t("edit-website.stats"), icon: "bi-bar-chart" },
            { key: "testimonials", label: t("edit-website.testimonials"), icon: "bi-chat-quote" },
            { key: "contact", label: t("edit-website.contact"), icon: "bi-envelope" },
          ].map((s) => <SectionToggle key={s.key} sectionKey={s.key} label={s.label} icon={s.icon} />)}

          <p className="sidebar-group-label" style={{ ...styles.sidebarGroupLabel, marginTop: 16 }}>{t("edit-website.hidden_sections")}</p>
          {[
            { key: "gallery", label: t("edit-website.gallery"), icon: "bi-images" },
            { key: "faq", label: t("edit-website.faq"), icon: "bi-question-circle" },
            { key: "team", label: t("edit-website.team"), icon: "bi-people" },
            { key: "products", label: t("edit-website.products"), icon: "bi-bag" },
          ].map((s) => <SectionToggle key={s.key} sectionKey={s.key} label={s.label} icon={s.icon} />)}
        </div>

        <div className="edit-content" style={styles.content}>{renderSection()}</div>
      </div>

      {/* ─── FLOATING BACK TO DASHBOARD BUTTON ─────────────────────────────── */}
      <button
        onClick={() => navigate('/dashboard')}
        style={styles.floatingBack}
        title="Back to Dashboard"
      >
        <i className="bi bi-x-lg" />
      </button>

      {/* Mobile Bottom Sheet */}
      {isMobile && showSectionModal && (
        <div className="mobile-section-modal" style={styles.mobileModalOverlay} onClick={() => setShowSectionModal(false)}>
          <div style={styles.mobileModalSheet} onClick={(e) => e.stopPropagation()}>
            <div style={styles.sheetHandle} />
            <div style={styles.sheetHeader}>
              <span style={{ fontWeight: 600, fontSize: 15 }}>{sectionLabels[activeSection] || activeSection}</span>
              <div style={{ display: "flex", gap: 8 }}>
                <button style={styles.btnSaveSheet} onClick={handleSave} disabled={saving}>
                  {saving ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-floppy" /> {t("edit-website.save")}</>}
                </button>
                <button style={styles.btnCloseSheet} onClick={() => setShowSectionModal(false)}><i className="bi bi-x-lg" /></button>
              </div>
            </div>
            <div style={styles.sheetContent}>{renderSection()}</div>
          </div>
        </div>
      )}

      {/* Media Picker Modal */}
      {showMediaModal && (
        <div style={styles.modalOverlay} onClick={() => setShowMediaModal(false)}>
          <div style={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h6 style={{ margin: 0, fontWeight: 600 }}>{t("edit-website.pick_image")}</h6>
              <button style={styles.btnModalClose} onClick={() => setShowMediaModal(false)}><i className="bi bi-x-lg" /></button>
            </div>
            <div style={styles.modalBody}>
              {media.length === 0 ? (
                <div style={styles.emptyMedia}>
                  <i className="bi bi-image" style={{ fontSize: 36, color: "#ddd" }} />
                  <p style={{ color: "#aaa", marginTop: 8, fontSize: 13 }}>{t("edit-website.no_images_modal")}</p>
                </div>
              ) : (
                <div style={styles.mediaGrid}>
                  {media.map((img) => (
                    <div key={img.id} style={{ ...styles.mediaItem, cursor: "pointer" }} onClick={() => selectMediaImage(img.url)}>
                      <img src={img.url} alt={img.filename} style={styles.mediaThumbnail} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="bottom-right" autoClose={3000} />
    </>
  );
}

const styles = {
  loadingWrap: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "80vh" },
  topbar: { display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #e5e5e5", padding: "20px", position: "sticky", top: 0, zIndex: 100, background: "var(--glass)", backdropFilter: "blur(10px)", webkitBackdropFilter: "blur(10px)" },
  topbarLeft: { display: "flex", alignItems: "center", gap: 8 },
  topbarRight: { display: "flex", gap: 8 },
  logo: { fontSize: "26px", fontWeight: "800", color: "var(--secondary-red)", letterSpacing: "-1px", textDecoration: "none" },
  dividerV: { width: 1, height: 18, background: "#ddd" },
  btnPreview: { background: "transparent", border: "1px solid #ddd", borderRadius: 8, padding: "6px 14px", fontSize: 13, color: "#555", textDecoration: "none", display: "flex", alignItems: "center", gap: 5 },
  btnSave: { background: "#7C3AED", color: "white", border: "none", borderRadius: 8, padding: "6px 16px", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 },
  main: { display: "flex", minHeight: "calc(100vh - 56px)", backgroundColor: "#fff" },
  sidebar: { width: 200, background: "white", borderRight: "1px solid #e5e5e5", padding: 12, paddingBottom: 100, flexShrink: 0, overflowY: "auto" },
  sidebarGroupLabel: { fontSize: 10, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.06em", padding: "4px 10px 6px", margin: 0 },
  sidebarItem: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", borderRadius: 7, fontSize: 15, color: "black", cursor: "pointer", marginBottom: 2, fontWeight: "bold", transition: "background 0.15s" },
  sidebarItemActive: { background: "#7C3AED18", color: "#7C3AED", fontWeight: 600 },
  sidebarItemDimmed: { opacity: 0.45 },
  toggle: { width: 32, height: 18, borderRadius: 9, position: "relative", cursor: "pointer", flexShrink: 0, transition: "background 0.2s" },
  toggleDot: { width: 14, height: 14, background: "white", borderRadius: "50%", position: "absolute", top: 2, transition: "left 0.2s" },
  toggleRow: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", marginBottom: 4, borderBottom: "1px solid #eee" },
  toggleLabel: { fontSize: 12, fontWeight: 600, color: "#555", display: "flex", alignItems: "center", lineHeight: 1 },
  content: { flex: 1, padding: 24, overflowY: "auto", maxHeight: "calc(100vh - 56px)" },
  sectionTitle: { fontSize: 15, fontWeight: 600, color: "#1a1a1a", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 },
  subTitle: { fontSize: 13, fontWeight: 600, color: "#333", marginBottom: 12 },
  fieldGroup: { display: "flex", flexDirection: "column", gap: 4, marginBottom: 14 },
  fieldLabel: { fontSize: 12, color: "#888", fontWeight: 500 },
  input: { background: "#f9f9f9", border: "1px solid #e5e5e5", borderRadius: 8, padding: "8px 12px", fontSize: 16, color: "#1a1a1a", width: "100%", transition: "border 0.15s" },
  textarea: { background: "#f9f9f9", border: "1px solid #e5e5e5", borderRadius: 8, padding: "8px 12px", fontSize: 16, color: "#1a1a1a", width: "100%", resize: "vertical", transition: "border 0.15s", fontFamily: "inherit" },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  card: { background: "#f9f9f9", border: "1px solid #eee", borderRadius: 10, padding: 14, marginBottom: 12, transition: "opacity 0.2s" },
  cardHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 },
  cardNum: { fontSize: 11, color: "#aaa", margin: 0, fontWeight: 600 },
  colorRow: { display: "flex", alignItems: "center", gap: 10 },
  colorPicker: { width: 40, height: 36, border: "none", borderRadius: 6, cursor: "pointer", padding: 0 },
  colorVal: { fontSize: 12, color: "#888" },
  divider: { border: "none", borderTop: "1px solid #eee", margin: "16px 0" },
  imageFieldWrap: { display: "flex", flexDirection: "column", gap: 6 },
  imagePreview: { width: "100%", maxHeight: 160, objectFit: "cover", borderRadius: 8, border: "1px solid #eee" },
  btnPickMedia: { background: "#7C3AED", color: "white", border: "none", borderRadius: 8, padding: "0 12px", fontSize: 16, cursor: "pointer", flexShrink: 0, height: 42 },
  btnUpload: { background: "#7C3AED", color: "white", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 },
  emptyMedia: { textAlign: "center", padding: "40px 20px" },
  mediaGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 },
  mediaItem: { position: "relative", borderRadius: 8, overflow: "hidden", aspectRatio: "1", background: "#f0f0f0" },
  mediaThumbnail: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  btnDeleteMedia: { position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.6)", color: "white", border: "none", borderRadius: "50%", width: 22, height: 22, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 },
  modalBox: { background: "white", borderRadius: 12, width: "90%", maxWidth: 540, maxHeight: "80vh", display: "flex", flexDirection: "column", overflow: "hidden" },
  modalHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: "1px solid #eee" },
  modalBody: { padding: 16, overflowY: "auto", flex: 1 },
  btnModalClose: { background: "transparent", border: "none", fontSize: 16, cursor: "pointer", color: "#555" },
  siteTitle: { fontSize: "20px", fontWeight: "bold", color: "purple", paddingLeft: 12 },
  mobileModalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9998, display: "flex", alignItems: "flex-end" },
  mobileModalSheet: { background: "white", borderRadius: "20px 20px 0 0", width: "100%", maxHeight: "85vh", display: "flex", flexDirection: "column", boxShadow: "0 -4px 20px rgba(0,0,0,0.15)" },
  sheetHandle: { width: 40, height: 4, background: "#ddd", borderRadius: 2, margin: "12px auto 0", flexShrink: 0 },
  sheetHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px 12px", borderBottom: "1px solid #eee", flexShrink: 0 },
  sheetContent: { padding: "16px 16px 32px", overflowY: "auto", flex: 1 },
  btnSaveSheet: { background: "#7C3AED", color: "white", border: "none", borderRadius: 8, padding: "6px 14px", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 },
  btnCloseSheet: { background: "transparent", border: "1px solid #eee", borderRadius: 8, padding: "6px 10px", fontSize: 14, cursor: "pointer", color: "#555" },
  floatingBack: { position: "fixed", bottom: 24, right: 24, width: 48, height: 48, borderRadius: "50%", background: "#1a1a1a", color: "white", border: "none", fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 16px rgba(0,0,0,0.25)", zIndex: 9997, transition: "background 0.2s, transform 0.2s" },
};